import {
  CREATE_GRID_ROW,
  DELETE_GRID_ROW,
  HexVersionAtomicOperation,
  UPDATE_GRID_ROW,
  stableEmptySet,
} from "@hex/common";
import { useCallback } from "react";

import { DispatchAOResult } from "../../../../atomic-operations";
import { useHexVersionAOContext } from "../../../../util/hexVersionAOContext";
import { PartialGridRowsChange } from "../utils/gridUtils";

type ReturnVal = {
  dispatchGridRowChangesOperations: (
    changes: PartialGridRowsChange,
  ) => HexVersionAtomicOperation[];
  dispatchGridRowChanges: (changes: PartialGridRowsChange) => DispatchAOResult;
};
export const useDispatchGridRowChanges = (): ReturnVal => {
  const { dispatchAO } = useHexVersionAOContext();

  const dispatchGridRowChangesOperations = useCallback(
    ({
      createdGridRowIds = stableEmptySet(),
      gridLayoutId,
      gridRows,
      modifiedGridRowIds = stableEmptySet(),
      removedGridRowIds = stableEmptySet(),
    }: PartialGridRowsChange) => {
      const actions: HexVersionAtomicOperation[] = [];

      for (const rowId of createdGridRowIds) {
        const newRow = gridRows.find((r) => r.id === rowId);
        if (newRow == null) continue;
        actions.push(
          CREATE_GRID_ROW.create({
            gridLayoutId,
            gridRowId: newRow.id,
            gridColumns: newRow.gridColumns,
            insertAt: newRow.order,
          }),
        );
      }

      for (const rowId of modifiedGridRowIds) {
        const modifiedRow = gridRows.find((r) => r.id === rowId);
        if (modifiedRow == null) continue;
        actions.push(
          UPDATE_GRID_ROW.create(
            modifiedRow.id,
            "gridColumns",
            modifiedRow.gridColumns,
          ),
          UPDATE_GRID_ROW.create(
            modifiedRow.id,
            "gridLayoutId",
            modifiedRow.gridLayoutId,
          ),
        );
      }

      for (const rowId of removedGridRowIds) {
        actions.push(DELETE_GRID_ROW.create(rowId));
      }

      return actions;
    },
    [],
  );

  const dispatchGridRowChanges = useCallback(
    (changes: PartialGridRowsChange) => {
      return dispatchAO(dispatchGridRowChangesOperations(changes));
    },
    [dispatchAO, dispatchGridRowChangesOperations],
  );

  return { dispatchGridRowChanges, dispatchGridRowChangesOperations };
};
